import { lazy } from "react";
import type { ComponentType } from "react";

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500;
const RELOAD_KEY = "__chunk_reload_ts__";
const RELOAD_COUNT_KEY = "__chunk_reload_count__";
const MAX_RELOADS = 1;
const RELOAD_WINDOW = 60_000;

type ModuleImport = { default: ComponentType<any> };

function isChunkError(error: unknown): boolean {
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("unable to preload") ||
    msg.includes("importing a module script failed") ||
    msg.includes("is not a valid javascript mime type") ||
    msg.includes("load failed") ||
    msg.includes("failed to fetch")
  );
}

function extractUrlFromError(error: unknown): string | null {
  const msg = error instanceof Error ? error.message : String(error);
  const match = msg.match(/https?:\/\/[^\s"')]+\.js[^\s"')]*/) ||
    msg.match(/\/[^\s"')]+\.js[^\s"')]*/) ||
    msg.match(/https?:\/\/[^\s"')]+\.mjs[^\s"')]*/);
  return match ? match[0] : null;
}

function extractUrlFromFnSource(fn: () => Promise<ModuleImport>): string | null {
  try {
    const fnStr = fn.toString();
    const match = fnStr.match(/import\s*\(\s*["']([^"']+)["']\s*\)/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function buildCacheBustedImport(
  originalFn: () => Promise<ModuleImport>,
  lastError?: unknown,
): () => Promise<ModuleImport> {
  const url = extractUrlFromError(lastError) || extractUrlFromFnSource(originalFn);
  if (url) {
    const separator = url.includes("?") ? "&" : "?";
    const bustUrl = `${url}${separator}t=${Date.now()}`;
    return () => import(/* @vite-ignore */ bustUrl) as Promise<ModuleImport>;
  }
  return originalFn;
}

function canReload(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || "0");
    const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0");
    const now = Date.now();
    if (now - lastReload > RELOAD_WINDOW) {
      sessionStorage.setItem(RELOAD_COUNT_KEY, "0");
      return true;
    }
    return reloadCount < MAX_RELOADS;
  } catch {
    return false;
  }
}

function markReload(): void {
  try {
    const count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0");
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  } catch {}
}

function importWithRetry(
  importFn: () => Promise<ModuleImport>,
  retries = MAX_RETRIES,
): Promise<ModuleImport> {
  return importFn().catch((error: unknown) => {
    if (isChunkError(error)) {
      if (retries > 0) {
        return new Promise<ModuleImport>((resolve) => {
          setTimeout(() => {
            const nextFn = buildCacheBustedImport(importFn, error);
            resolve(importWithRetry(nextFn, retries - 1));
          }, RETRY_DELAY);
        });
      }
      if (canReload()) {
        markReload();
        window.location.reload();
        return new Promise<ModuleImport>(() => {});
      }
    }
    throw error;
  });
}

export function lazyRetry(importFn: () => Promise<ModuleImport>) {
  return lazy(() => importWithRetry(importFn));
}
